'use client';

import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Sparkles, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { ApiErrorMessage } from '@/components/ui/ErrorBoundary';
import { useImageGeneration } from '@/lib/hooks/useImageGeneration';

interface PromptForm {
  prompt: string;
  negativePrompt: string;
}

interface GeneratedImageResult {
  id: string;
  prompt: string;
  style: string;
  ratio: string;
  platform: string;
  color: string;
  b64_json: string;
  created: string;
  status: 'bereit' | 'generiert' | 'fehlgeschlagen';
}

const stylePresets = [
  { id: 'style-ind', value: 'Industriell', desc: 'Roh, metallisch, Werkstatt-Ästhetik', color: '#888', stylePrompt: 'raw industrial aesthetic, metallic textures, workshop atmosphere, dark moody lighting, gritty realism' },
  { id: 'style-min', value: 'Minimalist', desc: 'Clean, viel Weißraum, reduziert', color: '#ccc', stylePrompt: 'minimalist design, clean white space, reduced elements, elegant simplicity, modern typography' },
  { id: 'style-punk', value: 'Punk', desc: 'Neon, kontraststark, rebellisch', color: '#FF5F1F', stylePrompt: 'neon punk aesthetic, high contrast, rebellious energy, neon orange #FF5F1F accents, dark background, bold graphic style' },
  { id: 'style-corp', value: 'Corporate', desc: 'Professionell, seriös, vertrauenswürdig', color: '#0A66C2', stylePrompt: 'professional corporate style, trustworthy, serious tone, clean business aesthetic, blue tones' },
];

const aspectRatios = [
  { id: 'ratio-sq', value: '1:1', label: '1:1', desc: 'Instagram Feed', platform: 'Instagram', size: '1024x1024' },
  { id: 'ratio-port', value: '9:16', label: '9:16', desc: 'Stories / Reels', platform: 'Stories', size: '1024x1792' },
  { id: 'ratio-land', value: '1.91:1', label: '1.91:1', desc: 'LinkedIn / Facebook', platform: 'LinkedIn', size: '1792x1024' },
  { id: 'ratio-wide', value: '16:9', label: '16:9', desc: 'Blog Header', platform: 'Blog', size: '1280x896' },
];

interface VisualLabPromptPanelProps {
  onGeneratingChange?: (v: boolean) => void;
  onImagesGenerated?: (images: GeneratedImageResult[]) => void;
}

export default function VisualLabPromptPanel({ onGeneratingChange, onImagesGenerated }: VisualLabPromptPanelProps) {
  const [selectedStyle, setSelectedStyle] = useState('Punk');
  const [selectedRatio, setSelectedRatio] = useState('1:1');
  const [useBrandColors, setUseBrandColors] = useState(true);
  const [apiError, setApiError] = useState<string | null>(null);

  const { image, isLoading, error, generate } = useImageGeneration(
    'GEMINI',
    'gemini/gemini-2.5-flash-image'
  );

  const { register, handleSubmit, formState: { errors }, getValues } = useForm<PromptForm>({
    defaultValues: {
      prompt: 'Industrielle Fabrikhalle, modernes Unternehmen, Neon-Akzente, dunkler Hintergrund, Mittelstand-Atmosphäre, professionell aber rebellisch',
      negativePrompt: 'Stock-Foto-Look, generisch, überbelichtet, Menschen mit falschen Händen',
    },
  });

  // Notify parent of loading state changes
  useEffect(() => {
    onGeneratingChange?.(isLoading);
  }, [isLoading, onGeneratingChange]);

  // Handle error from hook
  useEffect(() => {
    if (error) {
      setApiError(error.message);
      toast.error('Bildgenerierung fehlgeschlagen');
    }
  }, [error]);

  // Handle successful image generation
  useEffect(() => {
    if (image && image.data && image.data.length > 0) {
      const stylePreset = stylePresets.find((s) => s.value === selectedStyle);
      const ratioPreset = aspectRatios.find((r) => r.value === selectedRatio);
      const now = new Date();
      const created = `${now.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })} · ${now.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`;

      const generatedImages: GeneratedImageResult[] = image.data.map((item: { b64_json: string }, idx: number) => ({
        id: `img-${Date.now()}-${idx}`,
        prompt: getValues('prompt'),
        style: selectedStyle,
        ratio: selectedRatio,
        platform: ratioPreset?.platform ?? 'Instagram',
        color: stylePreset?.color ?? '#FF5F1F',
        b64_json: item.b64_json,
        created,
        status: 'bereit' as const,
      }));

      onImagesGenerated?.(generatedImages);
      toast.success(`${generatedImages.length} Bild${generatedImages.length > 1 ? 'er' : ''} generiert`, {
        description: `Stil: ${selectedStyle} · Format: ${selectedRatio}`,
      });
    }
  }, [image]);

  const buildPrompt = (userPrompt: string, negativePrompt: string): string => {
    const stylePreset = stylePresets.find((s) => s.value === selectedStyle);
    const ratioPreset = aspectRatios.find((r) => r.value === selectedRatio);

    let fullPrompt = `${userPrompt}. Style: ${stylePreset?.stylePrompt ?? ''}. Platform-optimized for ${ratioPreset?.platform ?? 'social media'} (${selectedRatio} aspect ratio).`;

    if (useBrandColors) {
      fullPrompt += ' Brand colors: neon orange #FF5F1F on dark black #1C1C1C background.';
    }

    if (negativePrompt) {
      fullPrompt += ` Avoid: ${negativePrompt}.`;
    }

    fullPrompt += ' High quality, professional, photorealistic or graphic design style.';
    return fullPrompt;
  };

  const onGenerate = (data: PromptForm) => {
    setApiError(null);
    const ratioPreset = aspectRatios.find((r) => r.value === selectedRatio);
    let fullPrompt = buildPrompt(data.prompt, data.negativePrompt);
    generate(fullPrompt, { size: ratioPreset?.size ?? '1024x1024' });
  };

  return (
    <form
      onSubmit={handleSubmit(onGenerate)}
      className="space-y-4"
      aria-label="Bildgenerierung Formular"
      noValidate
    >
      {/* API Error */}
      {apiError && (
        <ApiErrorMessage
          title="Bildgenerierung fehlgeschlagen"
          description={apiError}
          onRetry={() => setApiError(null)}
        />
      )}

      {/* Prompt */}
      <div className="punk-card p-5">
        <p className="section-label mb-3">Bildprompt</p>

        <div className="mb-4">
          <label className="label-punk" htmlFor="prompt">Beschreibung</label>
          <p className="text-xs text-muted-2 mb-2" id="prompt-desc">Beschreibe das gewünschte Bild auf Deutsch oder Englisch.</p>
          <textarea
            id="prompt"
            className="textarea-punk"
            rows={5}
            aria-describedby="prompt-desc prompt-error"
            aria-invalid={!!errors.prompt}
            aria-required="true"
            {...register('prompt', { required: 'Prompt ist erforderlich' })}
          />
          {errors.prompt && (
            <p
              id="prompt-error"
              className="text-xs text-red-400 mt-1 mono"
              role="alert"
              aria-live="polite"
            >
              {errors.prompt.message}
            </p>
          )}
        </div>

        <div>
          <label className="label-punk" htmlFor="negativePrompt">Negativ-Prompt</label>
          <p className="text-xs text-muted-2 mb-2" id="neg-prompt-desc">Was soll nicht im Bild erscheinen?</p>
          <textarea
            id="negativePrompt"
            className="textarea-punk"
            rows={2}
            aria-describedby="neg-prompt-desc"
            {...register('negativePrompt')}
          />
        </div>
      </div>

      {/* Style Presets */}
      <div className="punk-card p-5">
        <p className="section-label mb-3" id="style-label">Stil-Preset</p>
        <div
          className="grid grid-cols-2 gap-2"
          role="radiogroup"
          aria-labelledby="style-label"
        >
          {stylePresets.map((style) => (
            <button
              key={style.id}
              type="button"
              onClick={() => setSelectedStyle(style.value)}
              className={`p-3 rounded border text-left transition-all focus:outline-none focus:ring-2 focus:ring-accent ${
                selectedStyle === style.value
                  ? 'border-accent bg-accent/8' : 'border-border bg-surface hover:border-accent/40'
              }`}
              role="radio"
              aria-checked={selectedStyle === style.value}
              aria-label={`Stil: ${style.value} — ${style.desc}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <span className="w-2 h-2 rounded-full" style={{ background: style.color }} aria-hidden="true" />
                <span className={`text-xs font-700 ${selectedStyle === style.value ? 'text-accent' : 'text-white'}`}>
                  {style.value}
                </span>
              </div>
              <p className="text-xs text-muted leading-snug">{style.desc}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Aspect Ratio */}
      <div className="punk-card p-5">
        <p className="section-label mb-3" id="ratio-label">Seitenverhältnis</p>
        <div
          className="grid grid-cols-2 gap-2"
          role="radiogroup"
          aria-labelledby="ratio-label"
        >
          {aspectRatios.map((ratio) => (
            <button
              key={ratio.id}
              type="button"
              onClick={() => setSelectedRatio(ratio.value)}
              className={`p-2.5 rounded border text-center transition-all focus:outline-none focus:ring-2 focus:ring-accent ${
                selectedRatio === ratio.value
                  ? 'border-accent bg-accent/8' : 'border-border bg-surface hover:border-accent/40'
              }`}
              role="radio"
              aria-checked={selectedRatio === ratio.value}
              aria-label={`Seitenverhältnis ${ratio.label} — ${ratio.desc}`}
            >
              <span className={`mono text-xs font-700 block ${selectedRatio === ratio.value ? 'text-accent' : 'text-white'}`}>
                {ratio.label}
              </span>
              <span className="text-xs text-muted">{ratio.desc}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Brand Colors toggle */}
      <div className="punk-card p-4 flex items-center justify-between">
        <div>
          <p className="text-xs font-600 text-white" id="brand-colors-label">Brand-Farben verwenden</p>
          <p className="text-xs text-muted" id="brand-colors-desc">Neon-Orange #FF5F1F · Schwarz #1C1C1C</p>
        </div>
        <button
          type="button"
          onClick={() => setUseBrandColors(!useBrandColors)}
          className={`w-10 h-5 rounded-full border transition-all relative focus:outline-none focus:ring-2 focus:ring-accent ${
            useBrandColors ? 'bg-accent border-accent' : 'bg-surface border-border'
          }`}
          role="switch"
          aria-checked={useBrandColors}
          aria-labelledby="brand-colors-label"
          aria-describedby="brand-colors-desc"
        >
          <span
            className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${
              useBrandColors ? 'left-5' : 'left-0.5'
            }`}
            aria-hidden="true"
          />
          <span className="sr-only">{useBrandColors ? 'Brand-Farben aktiv' : 'Brand-Farben inaktiv'}</span>
        </button>
      </div>

      <button
        type="submit"
        disabled={isLoading}
        className="btn-primary w-full justify-center py-3 focus:outline-none focus:ring-2 focus:ring-accent"
        aria-label={isLoading ? 'Bild wird generiert…' : '1 Bild generieren'}
        aria-busy={isLoading}
      >
        {isLoading ? (
          <>
            <Loader2 size={14} className="animate-spin" aria-hidden="true" />
            Generiert Bild…
          </>
        ) : (
          <>
            <Sparkles size={14} aria-hidden="true" />
            Bild generieren
          </>
        )}
      </button>
    </form>
  );
}